import {
  useInfiniteQuery,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";

/**
 * Generic factory to build an infinite query hook for endpoints that:
 *  - accept a numeric `pageNumber` parameter
 *  - return an object with a `nextPage` field (number | null | undefined)
 *
 * Usage:
 * const useTracksInfiniteList = createInfiniteQueryHook({
 *   fetchPage: tracksControllerTracksList,
 *   getQueryKey: getTracksControllerTracksListQueryKey,
 * });
 */
export function createInfiniteQueryHook<
  BaseParams extends Record<string, unknown>,
  // Page type must have a nextPage-ish field to drive pagination
  PageData extends { nextPage?: number | null | undefined },
>(config: {
  fetchPage: (
    params: BaseParams & { pageNumber: number },
  ) => Promise<PageData> | PageData;
  getQueryKey: (params: BaseParams) => QueryKey;
  /**
   * Optional custom resolver if the API uses a different field name. Should return `undefined` when no further page exists.
   */
  getNextPageParam?: (lastPage: PageData) => number | undefined | null;
  /** Starting page number (default 0). */
  initialPageParam?: number;
}) {
  const {
    fetchPage,
    getQueryKey,
    getNextPageParam = (lastPage: PageData) =>
      lastPage.nextPage === null || lastPage.nextPage === undefined
        ? undefined
        : lastPage.nextPage,
    initialPageParam = 0,
  } = config;

  return function useGenericInfiniteQuery(
    params: BaseParams,
    options: Omit<
      UseInfiniteQueryOptions<
        PageData,
        unknown,
        InfiniteData<PageData, number>,
        QueryKey,
        number
      >,
      "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
    > = {},
  ): UseInfiniteQueryResult<InfiniteData<PageData, number>, unknown> {
    return useInfiniteQuery<
      PageData,
      unknown,
      InfiniteData<PageData, number>,
      QueryKey,
      number
    >({
      queryKey: getQueryKey(params),
      queryFn: ({ pageParam }) =>
        fetchPage({ ...params, pageNumber: pageParam as number }),
      getNextPageParam: (last) => getNextPageParam(last) ?? undefined,
      initialPageParam,
      ...options,
    });
  };
}
